import React, { ChangeEvent, useState } from "react";

import { fromHistoryMonthInput, toHistoryMonthInput } from "../../shared/helpers";
import { Pager, SectionCard } from "../ui";

type IRSectionProps = {
  rows: Array<{
    sourceIndex: number;
    item: {
      id: string;
      name?: string | null;
      title?: string | null;
      published_at?: string | null;
      file_name?: string | null;
      file_storage_path?: string | null;
      file_url?: string | null;
    };
  }>;
  page: number;
  totalPages: number;
  start: number;
  files: Record<string, File | null>;
  canUpload: boolean;
  uploadGuardReason: string;
  onPageChange: (page: number) => void;
  onUpdate: (absoluteIndex: number, key: string, value: unknown) => void;
  onFileSelect: (itemId: string, file: File | null) => void;
  onAdd: () => void;
  onRemove: (absoluteIndex: number) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onSave: () => void;
  saving: boolean;
};

/**
 * IR 편집 섹션.
 * 페이지 단위 항목 편집, PDF 업로드, 현재 페이지 내 드래그 정렬을 제공한다.
 */
export function IRSection({
  rows,
  page,
  totalPages,
  start,
  files,
  canUpload,
  uploadGuardReason,
  onPageChange,
  onUpdate,
  onFileSelect,
  onAdd,
  onRemove,
  onReorder,
  onSave,
  saving,
}: IRSectionProps) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  return (
    <div className="cm-panel-grid">
      <div className="cm-actions">
        <button className="cm-btn cm-btn-secondary" onClick={onAdd} type="button">
          IR 추가
        </button>
        <button className="cm-btn" disabled={saving} onClick={onSave} type="button">
          {saving ? "저장 중..." : "IR 저장"}
        </button>
      </div>
      {!canUpload ? <div className="cm-info">{uploadGuardReason}</div> : null}

      {rows.map((row, localIndex) => {
        const absoluteIndex = row.sourceIndex;
        const item = row.item;
        const isDragOver = dragOver === absoluteIndex;
        const isDragging = dragging === absoluteIndex;

        return (
          <div
            className={`cm-history-card-wrap ${isDragOver ? "cm-drop-indicator-card" : ""}`.trim()}
            key={item.id}
            onDragOver={(event) => {
              if (dragging == null) return;
              event.preventDefault();
              setDragOver(absoluteIndex);
            }}
            onDrop={(event) => {
              event.preventDefault();
              if (dragging == null) return;
              onReorder(dragging, absoluteIndex);
              setDragging(null);
              setDragOver(null);
            }}
          >
            <SectionCard
              className={`cm-ir-card ${isDragging ? "is-dragging" : ""}`.trim()}
              title={
                <span className="cm-inline-row">
                  <span>{`IR #${start + localIndex + 1}`}</span>
                  <button
                    className="cm-drag-handle"
                    draggable
                    onDragStart={() => {
                      setDragging(absoluteIndex);
                    }}
                    onDragEnd={() => {
                      setDragging(null);
                      setDragOver(null);
                    }}
                    title="드래그 정렬"
                    type="button"
                  >
                    ☰
                  </button>
                </span>
              }
              actions={
                <button
                  className="cm-btn cm-btn-danger"
                  onClick={() => {
                    if (!window.confirm("이 IR 항목을 삭제하시겠습니까?")) return;
                    onRemove(absoluteIndex);
                  }}
                  type="button"
                >
                  삭제
                </button>
              }
            >
              <div className="cm-ir-grid">
                <div className="cm-field">
                  <label htmlFor={`cm-ir-name-${item.id}`}>이름</label>
                  <input className="cm-input" id={`cm-ir-name-${item.id}`} onChange={(event) => onUpdate(absoluteIndex, "name", event.target.value)} value={item.name || ""} />
                </div>
                <div className="cm-field">
                  <label htmlFor={`cm-ir-title-${item.id}`}>title</label>
                  <input className="cm-input" id={`cm-ir-title-${item.id}`} onChange={(event) => onUpdate(absoluteIndex, "title", event.target.value)} value={item.title || ""} />
                  <div className="cm-help">사용자 페이지의 IR 버튼 텍스트로 노출됩니다.</div>
                </div>
                <div className="cm-field">
                  <label htmlFor={`cm-ir-published-${item.id}`}>발행일</label>
                  <input
                    className="cm-input"
                    id={`cm-ir-published-${item.id}`}
                    onChange={(event) => onUpdate(absoluteIndex, "published_at", fromHistoryMonthInput(event.target.value))}
                    type="month"
                    value={toHistoryMonthInput(item.published_at || "")}
                  />
                </div>
                <div className="cm-field">
                  <label htmlFor={`cm-ir-file-${item.id}`}>PDF 파일</label>
                  <input
                    accept="application/pdf"
                    className="cm-input"
                    disabled={!canUpload}
                    id={`cm-ir-file-${item.id}`}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onFileSelect(item.id, event.target.files?.[0] || null)}
                    type="file"
                  />
                  <div className="cm-muted">선택 파일: {files[item.id]?.name || "(없음)"}</div>
                  <div className="cm-muted">현재 파일: {item.file_name || "(없음)"}</div>
                </div>
                <div className="cm-file-meta">
                  <div className="cm-path">file_storage_path: {item.file_storage_path || "(없음)"}</div>
                  <div className="cm-path">file_url: {item.file_url || "(없음)"}</div>
                </div>
              </div>
            </SectionCard>
          </div>
        );
      })}
      <Pager page={page} totalPages={totalPages} onPageChange={onPageChange} />
    </div>
  );
}
